"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import styled from "@emotion/styled"
import { Box, ButtonBase, Typography } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import "swiper/css/navigation"
import { Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  mediaQueries,
  sizes,
  transitionDelayTime,
  useInViewConfig,
  useIsDesktop,
  useIsTabletOnly,
  useTransitionUpStyles,
} from "ui"
import CommonTitle from "ui/common/bit/CommonTitle"
import { colors } from "ui/theme/colors"
import { Item } from "utils"
import { sendBlockEvent } from "utils/gtm"
import BlockWrapper from "../common/BlockWrapper"
import { Client } from "./PersonCardsList/PersonCardsList"

const StyledSwiper = styled(Swiper)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: stretch;
`
const StyledArrowContainer = styled(Box)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 36px;
  margin-bottom: ${sizes.baseQuadric};
  padding-top: 0;
  ${mediaQueries.desktopM} {
    margin-bottom: 0;
  }
  ${mediaQueries.desktopXl} {
  }
`
const StyledArrowIconWrapper = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  z-index: 1;
  cursor: pointer;
  &.swiper-button-prev-a {
    transform: rotate(180deg);
  }
  svg {
    width: 48px;
    height: 48px;
    ${mediaQueries.desktopXl} {
      width: 52px;
      height: 52px;
    }
  }
`

const StyledBgImageContainer = styled(Box)`
  position: absolute;
  bottom: 0;
  width: 250%;
  height: 250%;
  transform: translate(-32%, 26%);
  ${mediaQueries.tablet} {
    right: 0;
    width: 100%;
    height: 100%;
    transform: none;
  }
`

const StyledClientsContainer = styled(Box)`
  margin-top: -50px;
`

const StyledSwiperSlide = styled(SwiperSlide)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: auto;
  height: 100%;
  &.swiper-slide {
    height: 100%;
  }
  ${mediaQueries.tablet} {
    min-width: 375px;
    width: 100%;
  }
  ${mediaQueries.desktopM} {
    width: 375px;
    min-width: 32%;
  }
  ${mediaQueries.desktop} {
    width: 400px;
    min-width: 32%;
  }
`

const StyledSlideContent = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding-top: 64px;
`

const StyledImageContainer = styled(Box)`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 116px;
  z-index: 1;
  top: 0;
  left: 50%;
  transform: translateX(-50%);

  img {
    object-fit: contain;
  }
`

const StyledContentWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 400px;
  height: 100%;
  padding: 64px 50px 32px;
  border-radius: ${sizes.borderRadius};
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 9.772px -9.772px 36.646px 0px rgba(15, 36, 79, 0.08);
  backdrop-filter: blur(20.76602554321289px);
  gap: ${sizes.baseQuadric};
  ${mediaQueries.desktopM} {
    gap: ${sizes.baseTriple};
    border-radius: ${sizes.borderRadiusLarge};
  }
  ${mediaQueries.desktop} {
    max-width: 500px;
  }
  ${mediaQueries.desktopXl} {
    gap: ${sizes.baseQuadric};
  }
`
const StyledTypo = styled(Typography)`
  margin-top: auto;
  font-weight: 700;
  font-size: 14px;
  line-height: 18.2px;
  color: ${colors.black};
  ${mediaQueries.desktopXl} {
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
  }
  ${mediaQueries.desktopXl} {
    font-weight: 400;
    font-size: 18px;
    line-height: 27px;
  }
`
const StyledText = styled(Typography)`
  display: flex;
  font-weight: 400;
  font-size: 19px;
  line-height: 26.6px;
  height: 100%;
  text-align: center;
  align-items: center;
  justify-content: center;
  color: ${colors.black};
  ${mediaQueries.desktopXl} {
    font-weight: 400;
    font-size: 24px;
    line-height: 33.6px;
  }
`
interface ClientsProps {
  data: any
  clients: Item<Client>[]
  id?: string
}
const Clients = ({ data, clients, id }: ClientsProps) => {
  const transformedData = data
  const NextPrevArrowIcon = iconsMap["arrow_lightOrange_black_line"]
  const isDesktop = useIsDesktop()
  const isTablet = useIsTabletOnly()
  const { ref, inView } = useInView(useInViewConfig)
  const { title, subtitle, text } = transformedData
  const ditransitiveStyles = useTransitionUpStyles(inView, transitionDelayTime)
  const numSlidersPerView = isTablet ? 2 : isDesktop ? 3 : 1 // tablet: 2, desktop: 3, mobile: 1 per view
  return (
    <BlockWrapper id={id}>
      <StyledBgImageContainer ref={ref}>
        <Image fill src="/bgClients.svg" alt="bgClients" />
      </StyledBgImageContainer>
      <CommonTitle
        title={title?.value}
        subtitle={subtitle?.value}
        mobileTitle={title?.value}
        mobileSubtitle={subtitle?.value}
        text={text?.value}
      />
      <StyledClientsContainer ref={ref} style={{ ...ditransitiveStyles }}>
        <StyledSwiper
          navigation={{
            nextEl: ".swiper-button-next-a",
            prevEl: ".swiper-button-prev-a",
          }}
          autoHeight
          spaceBetween={30}
          modules={[Navigation]}
          slidesPerGroup={1}
          loop={isDesktop ? false : true}
          centeredSlides={isDesktop ? false : true}
          slidesPerView={
            numSlidersPerView < clients?.length
              ? numSlidersPerView
              : clients?.length
          }
        >
          {clients?.map((client, index) => {
            return (
              <StyledSwiperSlide key={`client-StyledSwiperSlide-${index}`}>
                <StyledSlideContent>
                  {client?.image && (
                    <StyledImageContainer>
                      <Image
                        fill
                        src={client.image?.url}
                        alt={client?.clientName}
                      />
                    </StyledImageContainer>
                  )}
                  <StyledContentWrapper>
                    <StyledText variant="body2">{client?.text}</StyledText>
                    <StyledTypo variant="body1">
                      {client?.clientName}
                    </StyledTypo>
                  </StyledContentWrapper>
                </StyledSlideContent>
              </StyledSwiperSlide>
            )
          })}
        </StyledSwiper>
        {clients.length > numSlidersPerView && (
          <StyledArrowContainer>
            <StyledArrowIconWrapper
              className="swiper-button-prev-a"
              onClick={() => {
                sendBlockEvent({
                  blockId: id || "7",
                  componentName: "עדות לקוח",
                  title: title?.value,
                  buttonName: "icon arrow",
                  productName: data?.formDefaultInsuranceType,
                })
              }}
            >
              <NextPrevArrowIcon />
            </StyledArrowIconWrapper>
            <StyledArrowIconWrapper
              className="swiper-button-next-a"
              onClick={() => {
                sendBlockEvent({
                  blockId: id || "7",
                  componentName: "עדות לקוח",
                  title: title?.value,
                  buttonName: "icon arrow",
                  productName: data?.formDefaultInsuranceType,
                })
              }}
            >
              <NextPrevArrowIcon />
            </StyledArrowIconWrapper>
          </StyledArrowContainer>
        )}
      </StyledClientsContainer>
    </BlockWrapper>
  )
}

export default Clients
